/**
 * Documentos de demonstração. Espelham a semente da migração `0017`.
 *
 * O corpo emitido fica NO documento, e não no modelo: é o texto que a paciente
 * assinou, e relê-lo pelo modelo vivo reescreveria o passado — a mesma regra
 * das fichas preenchidas.
 */

import type { AssinantesDoModelo, CategoriaDoModelo, StatusDoDocumento } from './tipos';

export interface ModeloSemente {
    id: string;
    nome: string;
    categoria: CategoriaDoModelo;
    assinantes: AssinantesDoModelo;
    corpo: string;
    publicado: boolean;
    favorito: boolean;
    atualizadoEm: string;
}

export interface DocumentoSemente {
    id: string;
    titulo: string;
    tipo: string;
    contatoId: string;
    paciente: string;
    modeloId?: string;
    corpo?: string;
    assinantes?: string;
    canal?: 'whatsapp' | 'email';
    status: StatusDoDocumento;
    paginas: number;
    criadoEm: string;
    atualizadoEm: string;
    enviadoEm?: string;
    assinadoEm?: string;
    recusadoEm?: string;
    motivoDaRecusa?: string;
    profissional?: string;
    procedimento?: string;
}

export const MODELOS_DE_DOCUMENTO: ModeloSemente[] = [
    {
        id: 'mdd-1', nome: 'Termo de consentimento — Toxina botulínica', categoria: 'Consentimento',
        assinantes: 'Paciente + profissional', publicado: true, favorito: true, atualizadoEm: '2026-09-02T10:00:00-03:00',
        corpo: 'Eu, {paciente}, portador(a) do CPF {cpf}, declaro estar ciente do procedimento {procedimento}, a ser realizado em {data} por {profissional}.\n\nFui informado(a) sobre a técnica, os resultados esperados, o tempo de duração do efeito e os cuidados posteriores. Declaro ter esclarecido todas as minhas dúvidas.\n\n{clinica}',
    },
    {
        id: 'mdd-2', nome: 'Contrato de pacote de sessões', categoria: 'Contrato',
        assinantes: 'Paciente + clínica', publicado: true, favorito: true, atualizadoEm: '2026-08-28T10:00:00-03:00',
        corpo: 'Contrato de prestação de serviços entre {clinica} e {paciente}, CPF {cpf}.\n\nObjeto: pacote de {procedimento}, no valor total de {valor}, com início em {data}.\n\nAs sessões têm validade de 12 meses a contar da assinatura. Faltas não avisadas com 24h de antecedência consomem a sessão.',
    },
    {
        id: 'mdd-3', nome: 'Anamnese facial', categoria: 'Formulário',
        assinantes: 'Paciente', publicado: true, favorito: false, atualizadoEm: '2026-08-19T10:00:00-03:00',
        corpo: 'Paciente: {paciente}\nData: {data}\nProfissional: {profissional}\n\n1. Queixa principal:\n2. Procedimentos estéticos anteriores:\n3. Alergias e sensibilidades:\n4. Uso de medicamentos:\n5. Gestante ou lactante:\n6. Rotina de cuidados com a pele:',
    },
    {
        id: 'mdd-4', nome: 'Termo de uso de imagem', categoria: 'Consentimento',
        assinantes: 'Paciente', publicado: true, favorito: false, atualizadoEm: '2026-08-11T10:00:00-03:00',
        corpo: 'Eu, {paciente}, autorizo {clinica} a utilizar imagens do procedimento {procedimento} realizado em {data} para fins de divulgação em materiais e redes sociais, sem finalidade comercial de venda das imagens.',
    },
    {
        id: 'mdd-5', nome: 'Orientações pós-procedimento — Peeling', categoria: 'Orientação',
        assinantes: 'Não requer assinatura', publicado: true, favorito: false, atualizadoEm: '2026-08-05T10:00:00-03:00',
        corpo: 'Orientações para {paciente} após {procedimento} em {data}:\n\n• Evitar exposição solar por 7 dias e usar FPS 50+ a cada 3 horas.\n• Não manipular a descamação.\n• Suspender ácidos por 5 dias.\n• Em caso de ardência intensa, entrar em contato com a clínica.',
    },
    {
        /* Rascunho de propósito: é o caso que prova que modelo não publicado
           não aparece na hora de emitir. */
        id: 'mdd-6', nome: 'Termo de cancelamento e reembolso', categoria: 'Contrato',
        assinantes: 'Paciente + clínica', publicado: false, favorito: false, atualizadoEm: '2026-07-30T10:00:00-03:00',
        corpo: 'Rascunho — revisar cláusulas de reembolso com o jurídico antes de publicar.\n\nPaciente: {paciente}\nValor: {valor}',
    },
];

export const DOCUMENTOS_EMITIDOS: DocumentoSemente[] = [
    {
        id: 'doc-1', titulo: 'Termo de consentimento — Botox', tipo: 'Termo de consentimento',
        contatoId: 'p-clara', paciente: 'Clara Ribeiro', modeloId: 'mdd-1', assinantes: 'Paciente + profissional',
        canal: 'whatsapp', status: 'aguardando', paginas: 3, profissional: 'Ana Prado', procedimento: 'Toxina botulínica',
        criadoEm: '2026-09-08T09:12:00-03:00', atualizadoEm: '2026-09-08T09:12:00-03:00', enviadoEm: '2026-09-08T09:12:00-03:00',
        corpo: 'Eu, Clara Ribeiro, portador(a) do CPF 315.772.070-00, declaro estar ciente do procedimento Toxina botulínica, a ser realizado em 08/09/2026 por Ana Prado.\n\nFui informado(a) sobre a técnica, os resultados esperados, o tempo de duração do efeito e os cuidados posteriores. Declaro ter esclarecido todas as minhas dúvidas.\n\nVecchia — Jardins',
    },
    {
        id: 'doc-2', titulo: 'Contrato de pacote — 10 sessões', tipo: 'Contrato',
        contatoId: 'p-clara', paciente: 'Clara Ribeiro', modeloId: 'mdd-2', assinantes: 'Paciente + clínica',
        canal: 'whatsapp', status: 'assinado', paginas: 5, procedimento: 'Limpeza de pele',
        criadoEm: '2026-09-07T16:00:00-03:00', atualizadoEm: '2026-09-07T16:40:00-03:00',
        enviadoEm: '2026-09-07T16:00:00-03:00', assinadoEm: '2026-09-07T16:40:00-03:00',
        corpo: 'Contrato de prestação de serviços entre Vecchia — Jardins e Clara Ribeiro, CPF 315.772.070-00.\n\nObjeto: pacote de Limpeza de pele, no valor total de R$ 3.800,00, com início em 02/09/2026.\n\nAs sessões têm validade de 12 meses a contar da assinatura. Faltas não avisadas com 24h de antecedência consomem a sessão.',
    },
    {
        id: 'doc-3', titulo: 'Anamnese facial', tipo: 'Anamnese',
        contatoId: 'p-marina', paciente: 'Marina Duarte', modeloId: 'mdd-3', assinantes: 'Paciente',
        status: 'rascunho', paginas: 2, profissional: 'Paulo Menezes',
        criadoEm: '2026-09-02T11:20:00-03:00', atualizadoEm: '2026-09-02T11:20:00-03:00',
        corpo: 'Paciente: Marina Duarte\nData: 02/09/2026\nProfissional: Paulo Menezes\n\n1. Queixa principal:\n2. Procedimentos estéticos anteriores:\n3. Alergias e sensibilidades:\n4. Uso de medicamentos:\n5. Gestante ou lactante:\n6. Rotina de cuidados com a pele:',
    },
    {
        /* Parado na assinatura desde 04/09: é ele que faz o aviso de "há mais
           de 48h" existir, e é o documento que a recepção esquece. */
        id: 'doc-5', titulo: 'Termo de consentimento — Peeling', tipo: 'Termo de consentimento',
        contatoId: 'p-bruno', paciente: 'Bruno Sartori', modeloId: 'mdd-1', assinantes: 'Paciente + profissional',
        canal: 'whatsapp', status: 'aguardando', paginas: 2, profissional: 'Ana Prado', procedimento: 'Peeling',
        criadoEm: '2026-09-04T10:30:00-03:00', atualizadoEm: '2026-09-04T10:30:00-03:00', enviadoEm: '2026-09-04T10:30:00-03:00',
        corpo: 'Eu, Bruno Sartori, portador(a) do CPF 402.118.930-22, declaro estar ciente do procedimento Peeling, a ser realizado em 04/09/2026 por Ana Prado.\n\nFui informado(a) sobre a técnica, os resultados esperados, o tempo de duração do efeito e os cuidados posteriores. Declaro ter esclarecido todas as minhas dúvidas.\n\nVecchia — Jardins',
    },
    {
        /* O recusado existe para a tela ter o caso que dói: motivo gravado, e
           não só um status vermelho. */
        id: 'doc-4', titulo: 'Termo de imagem', tipo: 'Termo de imagem',
        contatoId: 'p-rafael', paciente: 'Rafael Antunes', modeloId: 'mdd-4', assinantes: 'Paciente',
        canal: 'whatsapp', status: 'rejeitado', paginas: 1, procedimento: 'Harmonização',
        criadoEm: '2026-08-29T13:00:00-03:00', atualizadoEm: '2026-08-29T14:05:00-03:00',
        enviadoEm: '2026-08-29T13:00:00-03:00', recusadoEm: '2026-08-29T14:05:00-03:00',
        motivoDaRecusa: 'Paciente não autorizou o uso das imagens.',
        corpo: 'Eu, Rafael Antunes, autorizo Vecchia — Jardins a utilizar imagens do procedimento Harmonização realizado em 29/08/2026 para fins de divulgação em materiais e redes sociais, sem finalidade comercial de venda das imagens.',
    },
];
